import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import React, { useState, useMemo } from "react";
import type { ContactDetails, ContactInteraction } from "@/hooks/useContactDetails";
import { format } from "date-fns";

interface InteractionProps {
  contact?: ContactDetails;
  interactions?: ContactInteraction[];
  onAddInteraction?: (title: string, description: string) => Promise<boolean>;
  onUpdateInteraction?: (interactionId: string, title: string, description: string) => Promise<boolean>;
  onDeleteInteraction?: (interactionId: string) => Promise<boolean>;
}

const Interaction: React.FC<InteractionProps> = ({ contact, interactions = [], onAddInteraction }) => {
  const [noteText, setNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!contact) return { diasAberta: 0, diasNaEtapa: 0, totalInteracoes: 0, diasSemInteracao: 0 };

    const now = new Date();
    const createdDate = new Date(contact.created_at);
    const diasAberta = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    // Get latest opportunity to calculate days in stage
    let diasNaEtapa = 0;
    if (contact.opportunities && contact.opportunities.length > 0) {
      const latestOpp = contact.opportunities[0];
      const oppUpdated = new Date(latestOpp.updated_at);
      diasNaEtapa = Math.floor((now.getTime() - oppUpdated.getTime()) / (1000 * 60 * 60 * 24));
    }

    const totalInteracoes = interactions.length;

    // Days without interaction
    let diasSemInteracao = diasAberta;
    if (contact.last_interaction_at) {
      const lastInteractionDate = new Date(contact.last_interaction_at);
      diasSemInteracao = Math.floor((now.getTime() - lastInteractionDate.getTime()) / (1000 * 60 * 60 * 24));
    }

    return { diasAberta, diasNaEtapa, totalInteracoes, diasSemInteracao };
  }, [contact, interactions]);

  const handleSaveNote = async () => {
    if (!noteText.trim() || !onAddInteraction) return;

    setIsSaving(true);
    try {
      const success = await onAddInteraction("Nota", noteText);
      if (success) {
        setNoteText("");
      }
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const kpiCard =
    "text-center rounded-xl border border-[#E5E7EB] py-3 px-4 bg-white shadow-sm transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#27272A] dark:shadow-none";
  const eventCard =
    "px-4 py-3 rounded-xl bg-white border border-[#E5E7EB] shadow-sm w-full transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none";

  return (
    <div className="w-full text-[#0F172A] order-3 xl:order-2 xl:col-span-1 md:col-span-2 col-span-1 dark:text-[#FAFAFA]">
      <div className="rounded-xl flex flex-col gap-4 p-4 border border-[#E5E7EB] bg-white shadow-sm transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#27272A] dark:shadow-none">
        <div className="grid grid-cols-2 gap-3">
          <div className={kpiCard}>
            <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white">{metrics.diasAberta}</h3>
            <p className="text-xs font-medium text-[#64748B] mt-1 dark:text-[#A1A1AA]">Dias aberta</p>
          </div>
          <div className={kpiCard}>
            <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white">{metrics.diasNaEtapa}</h3>
            <p className="text-xs font-medium text-[#64748B] mt-1 dark:text-[#A1A1AA]">Dias na etapa</p>
          </div>
          <div className={kpiCard}>
            <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white">{metrics.totalInteracoes}</h3>
            <p className="text-xs font-medium text-[#64748B] mt-1 dark:text-[#A1A1AA]">Interações</p>
          </div>
          <div className={kpiCard}>
            <h3 className="text-2xl font-bold text-[#0F172A] dark:text-white">{metrics.diasSemInteracao}</h3>
            <p className="text-xs font-medium text-[#64748B] mt-1 dark:text-[#A1A1AA]">Dias s/ interação</p>
          </div>
        </div>
        <Textarea
          placeholder="Digite uma anotação"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          className="min-h-[120px] rounded-xl bg-white border border-[#E5E7EB] text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] transition-colors duration-200 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
        />
        <Button
          onClick={handleSaveNote}
          disabled={!noteText.trim() || isSaving}
          className="self-end min-w-[120px] bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors duration-200"
        >
          {isSaving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
      <h4 className="mt-8 mb-6 text-xl font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Interações</h4>
      <div className="interactions relative flex flex-col gap-8">
        {interactions.length === 0 ? (
          <div className="text-center text-[#94A3B8] py-8 dark:text-[#71717A]">
            Nenhuma interação registrada
          </div>
        ) : (
          interactions.map((interaction) => (
            <div key={interaction.id} className="flex gap-4 items-start">
              <div className="text-center flex flex-col items-center gap-2 min-w-[60px]">
                <div className="w-8 h-8 rounded-full bg-[#EEF4FF] border-2 border-[#2563EB] flex items-center justify-center dark:bg-[rgba(37,99,235,.18)] dark:border-[#60A5FA]">
                  <div className="w-3 h-3 rounded-full bg-[#2563EB] dark:bg-[#60A5FA]" />
                </div>
                <div className="text-xs text-[#64748B] font-medium dark:text-[#A1A1AA] leading-tight">
                  <p>{format(new Date(interaction.created_at), "dd/MM/yyyy")}</p>
                  <p>{format(new Date(interaction.created_at), "HH:mm")}</p>
                </div>
              </div>
              <div className={eventCard}>
                <p className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{interaction.title}</p>
                {interaction.description && (
                  <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap dark:text-[#D4D4D8]">
                    {interaction.description}
                  </p>
                )}
              </div>
            </div>
          ))
        )}

        {contact && (
          <div className="flex gap-4 items-start">
            <div className="text-center flex flex-col items-center gap-2 min-w-[60px]">
              <div className="w-8 h-8 rounded-full bg-[#EEF4FF] border-2 border-[#2563EB] flex items-center justify-center dark:bg-[rgba(37,99,235,.18)] dark:border-[#60A5FA]">
                <div className="w-3 h-3 rounded-full bg-[#2563EB] dark:bg-[#60A5FA]" />
              </div>
              <div className="text-xs text-[#64748B] font-medium dark:text-[#A1A1AA] leading-tight">
                <p>{format(new Date(contact.created_at), "dd/MM/yyyy")}</p>
                <p>{format(new Date(contact.created_at), "HH:mm")}</p>
              </div>
            </div>
            <div className={`flex flex-col gap-3 w-full ${eventCard}`}>
              <p className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Contato Criado</p>
              {contact.campaigns && contact.campaigns.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contact.campaigns.map((campaign, idx) => (
                    <Badge
                      key={idx}
                      className="py-1 px-3 bg-[#DBEAFE] text-[#2563EB] border-0 dark:bg-blue-500/20 dark:text-[#60A5FA]"
                    >
                      {campaign}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interaction;
